/**
 * Apache Solr Service for Importerr
 * Product search and indexing via backend proxy
 */

// Backend API URL from environment (includes /api/ suffix)
const API_URL = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';
const SOLR_PROXY_BASE = API_URL.replace(/\/+$/, '') + '/proxy/solr';

// Schema version - increment when document structure changes
const SCHEMA_VERSION = 3;

// Cache expiry time (1 month / 30 days)
const CACHE_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Maps a product from Alibaba API to Solr document format
 */
function mapProductToSolrDocument(product, keyword, pageNum, totalPages) {
  const productId = product.offerId || product._id || product.sku;
  const normalizedKeyword = (keyword || '').toLowerCase().trim();
  const calcPayload = product.calculationPayload || {};
  const detailedInfo = product.detailed_info || {};
  const productSaleInfo = detailedInfo.productSaleInfo || {};
  const productShippingInfo = detailedInfo.productShippingInfo || {};
  const productSkuInfos = detailedInfo.productSkuInfos || [];
  const sellerData = product.sellerDataInfo || {};
  const promotionModel = product.promotionModel || {};
  const priceInfoObj = product.priceInfo || {};

  const calculatedPrice = product.finalPrice || product.sp || priceInfoObj.finalPrice || product.consign_price || 0;

  // Collect ALL image URLs
  const allImages = [];
  if (Array.isArray(product.imgUrl)) {
    allImages.push(...product.imgUrl.filter(Boolean));
  } else if (product.imgUrl) {
    allImages.push(product.imgUrl);
  }
  if (product.imageUrl && !allImages.includes(product.imageUrl)) {
    allImages.push(product.imageUrl);
  }
  const detailedImages = detailedInfo.image?.images || detailedInfo.images || [];
  if (Array.isArray(detailedImages)) {
    detailedImages.forEach(img => {
      if (img && !allImages.includes(img)) allImages.push(img);
    });
  }
  const primaryImage = allImages[0] || '';

  const solrDoc = {
    id: `${normalizedKeyword}_${productId}`,
    offerId: String(productId),
    sku: String(productId),
    subject: product.subject || '',
    subjectTrans: product.subjectTrans || '',
    title: product.subjectTrans || product.subject || product.title || '',
    imageUrl: primaryImage,
    imgUrl: allImages,
    isJxhy: product.isJxhy || false,
    isOnePsale: product.isOnePsale || false,
    isPatentProduct: product.isPatentProduct || false,
    isSelect: product.isSelect || false,
    sp: calculatedPrice,
    finalPrice: calculatedPrice,
    consign_price: calculatedPrice,
    priceInfo: safeStringify(priceInfoObj),
    ap: calcPayload.ap || 0,
    cp: calcPayload.cp || 0,
    ls: calcPayload.ls || 0,
    dw: calcPayload.dw || 0,
    vw: calcPayload.vw || 0,
    hsn: calcPayload.hsn || false,
    hsnTaxPercent: calcPayload.hsnTaxPercent || 0,
    calculationPayload: safeStringify(calcPayload),
    priceRangeList: safeStringify(productSaleInfo.priceRangeList || []),
    topCategoryId: String(product.topCategoryId || ''),
    secondCategoryId: String(product.secondCategoryId || ''),
    thirdCategoryId: String(product.thirdCategoryId || ''),
    categoryId: String(detailedInfo.categoryId || product.categoryId || ''),
    category: product.category || 'global',
    monthSold: Number(product.monthSold || product.month_sold || 0),
    month_sold: Number(product.monthSold || product.month_sold || 0),
    repurchaseRate: product.repurchaseRate || '',
    tradeScore: parseFloat(product.tradeScore) || 0,
    minOrderQuantity: Number(product.minOrderQuantity || product.moq || 0),
    moq: Number(product.minOrderQuantity || product.moq || 0),
    amountOnSale: Number(productSaleInfo.amountOnSale || 0),
    sellerIdentities: product.sellerIdentities || [],
    offerIdentities: product.offerIdentities || [],
    sellerDataInfo: safeStringify(sellerData),
    tradeMedalLevel: sellerData.tradeMedalLevel || '',
    compositeServiceScore: parseFloat(sellerData.compositeServiceScore) || 0,
    logisticsExperienceScore: parseFloat(sellerData.logisticsExperienceScore) || 0,
    disputeComplaintScore: parseFloat(sellerData.disputeComplaintScore) || 0,
    offerExperienceScore: parseFloat(sellerData.offerExperienceScore) || 0,
    afterSalesExperienceScore: parseFloat(sellerData.afterSalesExperienceScore) || 0,
    consultingExperienceScore: parseFloat(sellerData.consultingExperienceScore) || 0,
    repeatPurchasePercent: parseFloat(sellerData.repeatPurchasePercent) || 0,
    rating: parseFloat(product.tradeScore) || product.rating || product.avgStar || 0,
    supplierRating: parseFloat(sellerData.compositeServiceScore) || product.supplierRating || 0,
    sellerRating: product.sellerRating || parseFloat(sellerData.compositeServiceScore) || 0,
    avgStar: product.avgStar || product.rating || 0,
    hasPromotion: promotionModel.hasPromotion || false,
    promotionType: promotionModel.promotionType || '',
    promotionURL: product.promotionURL || '',
    productSkuInfos: safeStringify(productSkuInfos),
    variantsCount: productSkuInfos.length,
    variationsCount: productSkuInfos.length || product.variationsCount || 0,
    sendGoodsAddressText: productShippingInfo.sendGoodsAddressText || '',
    skuShippingDetails: safeStringify(productShippingInfo.skuShippingDetails || productShippingInfo.skuShippingInfoList || []),
    createDate: product.createDate || '',
    modifyDate: product.modifyDate || '',
    indexedAt: Date.now(),
    schemaVersion: SCHEMA_VERSION,
    searchKeyword: normalizedKeyword,
    pageNumber: pageNum,
    totalPages: totalPages,
    location: product.location || 'global',
    source: product.source || 'alibaba',
    handling_days: Number(product.handling_days || product.leadTime || 0) || 15,
    mainVideo: product.mainVideo || product.detailed_info?.mainVideo || null,
  };

  return solrDoc;
}

function safeStringify(obj) {
  try {
    return JSON.stringify(obj || {});
  } catch (e) {
    return '{}';
  }
}

function safeParse(str, defaultValue = null) {
  try {
    return str ? JSON.parse(str) : defaultValue;
  } catch (e) {
    return defaultValue;
  }
}

class SolrService {
  constructor() {
    this.proxyUrl = SOLR_PROXY_BASE;
  }

  async searchProducts(query, filters = {}) {
    try {
      const { page = 0, hitsPerPage = 50, category, minPrice, maxPrice, rating, dropshipping, skipVersionCheck = false } = filters;
      const normalizedQuery = (query || '').toLowerCase().trim();
      
      if (!normalizedQuery) {
        console.log('[SolrService] Empty query, returning empty results');
        return { hits: [], nbHits: 0, nbPages: 0, page: 0 };
      }

      const params = new URLSearchParams();
      params.append('q', '*:*');
      params.append('fq', `searchKeyword:"${normalizedQuery}"`);
      params.append('start', page * hitsPerPage);
      params.append('rows', hitsPerPage);
      params.append('wt', 'json');
      params.append('sort', 'month_sold desc, rating desc');

      if (category && category !== 'global') {
        params.append('fq', `category:"${category}"`);
      }
      if (minPrice !== undefined && maxPrice !== undefined) {
        params.append('fq', `sp:[${minPrice} TO ${maxPrice}]`);
      }
      if (rating) {
        params.append('fq', `rating:[${rating} TO *]`);
      }
      if (dropshipping) {
        params.append('fq', 'moq:1');
        params.append('fq', 'isOnePsale:true');
      }

      console.log('[SolrService] Searching:', { 
        query: normalizedQuery, 
        url: `${this.proxyUrl}/search?${params.toString()}` 
      });

      const response = await fetch(`${this.proxyUrl}/search?${params.toString()}`);
      
      if (!response.ok) {
        console.error('[SolrService] Search failed:', response.status, response.statusText);
        throw new Error(`Solr search failed: ${response.status}`);
      }

      const result = await response.json();
      const docs = result.response?.docs || [];
      const numFound = result.response?.numFound || 0;

      console.log('[SolrService] Response:', { 
        numFound, 
        docsReturned: docs.length,
        firstDocId: docs[0]?.id,
        firstDocIndexedAt: docs[0]?.indexedAt,
        firstDocSchemaVersion: docs[0]?.schemaVersion
      });

      if (!skipVersionCheck && docs.length > 0) {
        const firstDoc = docs[0];
        // Handle schemaVersion as array (Solr multi-valued field) or single value
        const rawSchemaVersion = firstDoc.schemaVersion;
        const docSchemaVersion = Array.isArray(rawSchemaVersion) ? rawSchemaVersion[0] : rawSchemaVersion;
        const docIndexedAt = Array.isArray(firstDoc.indexedAt) ? firstDoc.indexedAt[0] : firstDoc.indexedAt || 0;
        const isExpired = (Date.now() - docIndexedAt) > CACHE_EXPIRY_MS;
        const isOutdatedSchema = (docSchemaVersion || 1) < SCHEMA_VERSION;
        
        console.log('[SolrService] Cache validation:', {
          docSchemaVersion,
          currentSchemaVersion: SCHEMA_VERSION,
          isOutdatedSchema,
          docIndexedAt,
          ageMs: Date.now() - docIndexedAt,
          cacheExpiryMs: CACHE_EXPIRY_MS,
          isExpired
        });
        
        if (isOutdatedSchema || isExpired) {
          console.log('[SolrService] Cache invalid - needs reindex', { reason: isOutdatedSchema ? 'outdated schema' : 'expired' });
          return { hits: [], nbHits: 0, nbPages: 0, page: 0, needsReindex: true };
        }
      }

      return {
        hits: docs.map(doc => this.mapSolrDocToProduct(doc)),
        nbHits: numFound,
        nbPages: Math.ceil(numFound / hitsPerPage),
        page: page,
        needsReindex: false,
      };
    } catch (error) {
      console.error('[SolrService] Search error:', error.message);
      return { hits: [], nbHits: 0, nbPages: 0, page: 0 };
    }
  }

  mapSolrDocToProduct(doc) {
    const product = {
      objectID: doc.id,
      _id: doc.id,
      sku: doc.sku || doc.offerId || doc.id,
      offerId: doc.offerId || doc.sku || doc.id,
      title: doc.title || doc.subjectTrans || doc.subject || '',
      subject: doc.subject || '',
      subjectTrans: doc.subjectTrans || doc.title || '',
      sp: doc.sp || doc.finalPrice || 0,
      finalPrice: doc.finalPrice || doc.sp || 0,
      consign_price: doc.consign_price || doc.sp || 0,
      imgUrl: doc.imgUrl || doc.imageUrl || '',
      imageUrl: doc.imageUrl || doc.imgUrl || '',
      moq: doc.moq || doc.minOrderQuantity || 0,
      minOrderQuantity: doc.minOrderQuantity || doc.moq || 0,
      isOnePsale: doc.isOnePsale || false,
      isJxhy: doc.isJxhy || false,
      isPatentProduct: doc.isPatentProduct || false,
      isSelect: doc.isSelect || false,
      rating: doc.rating || 0,
      supplierRating: doc.supplierRating || doc.rating || 0,
      sellerRating: doc.sellerRating || doc.rating || 0,
      avgStar: doc.avgStar || doc.rating || 0,
      tradeScore: doc.tradeScore || 0,
      location: doc.location || 'global',
      source: doc.source || 'alibaba',
      month_sold: doc.month_sold || doc.monthSold || 0,
      monthSold: doc.monthSold || doc.month_sold || 0,
      handling_days: doc.handling_days || 15,
      variationsCount: doc.variationsCount || doc.variantsCount || 0,
      variantsCount: doc.variantsCount || doc.variationsCount || 0,
      repurchaseRate: doc.repurchaseRate || '',
      amountOnSale: doc.amountOnSale || 0,
      topCategoryId: doc.topCategoryId || '',
      secondCategoryId: doc.secondCategoryId || '',
      thirdCategoryId: doc.thirdCategoryId || '',
      categoryId: doc.categoryId || '',
      category: doc.category || 'global',
      sellerIdentities: doc.sellerIdentities || [],
      offerIdentities: doc.offerIdentities || [],
      sellerDataInfo: safeParse(doc.sellerDataInfo, {}),
      tradeMedalLevel: doc.tradeMedalLevel || '',
      compositeServiceScore: doc.compositeServiceScore || 0,
      logisticsExperienceScore: doc.logisticsExperienceScore || 0,
      disputeComplaintScore: doc.disputeComplaintScore || 0,
      offerExperienceScore: doc.offerExperienceScore || 0,
      afterSalesExperienceScore: doc.afterSalesExperienceScore || 0,
      consultingExperienceScore: doc.consultingExperienceScore || 0,
      repeatPurchasePercent: doc.repeatPurchasePercent || 0,
      hasPromotion: doc.hasPromotion || false,
      promotionType: doc.promotionType || '',
      promotionURL: doc.promotionURL || '',
      productSkuInfos: safeParse(doc.productSkuInfos, []),
      skuShippingDetails: safeParse(doc.skuShippingDetails, []),
      sendGoodsAddressText: doc.sendGoodsAddressText || '',
      createDate: doc.createDate || '',
      modifyDate: doc.modifyDate || '',
      indexedAt: doc.indexedAt || 0,
      searchKeyword: doc.searchKeyword || '',
      pageNumber: doc.pageNumber || 1,
      ap: doc.ap || 0,
      cp: doc.cp || 0,
      ls: doc.ls || 0,
      dw: doc.dw || 0,
      vw: doc.vw || 0,
      hsn: doc.hsn || false,
      hsnTaxPercent: doc.hsnTaxPercent || 0,
      calculationPayload: safeParse(doc.calculationPayload, null),
      priceInfo: safeParse(doc.priceInfo, null),
      priceRangeList: safeParse(doc.priceRangeList, []),
      mainVideo: doc.mainVideo || null,
    };

    return product;
  }

  async indexProducts(products, keyword = '', pageNum = 1, totalPages = 1) {
    try {
      if (!products || products.length === 0) {
        return { success: false, indexed: 0, skipped: 0 };
      }

      const normalizedKeyword = (keyword || '').toLowerCase().trim();
      const productIds = products.map(p => p.offerId || p._id || p.sku).filter(Boolean);
      const existingIds = new Set();
      
      try {
        const idQueries = productIds.map(id => `"${normalizedKeyword}_${id}"`).join(' OR ');
        const params = new URLSearchParams();
        params.append('q', `id:(${idQueries})`);
        params.append('fl', 'id,offerId');
        params.append('rows', productIds.length);
        params.append('wt', 'json');
        
        const response = await fetch(`${this.proxyUrl}/search?${params.toString()}`);
        if (response.ok) {
          const result = await response.json();
          const docs = result.response?.docs || [];
          docs.forEach(doc => {
            if (doc.offerId) existingIds.add(String(doc.offerId));
          });
        }
      } catch (err) {
        // Continue with indexing all products
      }
      
      const newProducts = products.filter(product => {
        const productId = String(product.offerId || product._id || product.sku);
        return !existingIds.has(productId);
      });
      
      if (newProducts.length === 0) {
        return { success: true, indexed: 0, skipped: products.length };
      }

      const docs = newProducts.map(product => mapProductToSolrDocument(product, keyword, pageNum, totalPages));
      
      // Debug: Log if any products have mainVideo
      const productsWithVideo = docs.filter(d => d.mainVideo);
      if (productsWithVideo.length > 0) {
        console.log(`[SolrService] Indexing ${productsWithVideo.length} products with mainVideo`);
      }

      const response = await fetch(`${this.proxyUrl}/index`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(docs),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Solr indexing failed: ${response.status} - ${errorText}`);
      }

      return { success: true, indexed: newProducts.length, skipped: existingIds.size };
    } catch (error) {
      return { success: false, indexed: 0, skipped: 0, error: error.message };
    }
  }

  async checkKeywordExists(keyword) {
    try {
      const normalizedKeyword = keyword.toLowerCase().trim();
      const params = new URLSearchParams();
      params.append('q', '*:*');
      params.append('fq', `searchKeyword:"${normalizedKeyword}"`);
      params.append('rows', 1);
      params.append('wt', 'json');

      const response = await fetch(`${this.proxyUrl}/search?${params.toString()}`);
      const result = await response.json();
      
      return (result.response?.numFound || 0) > 0;
    } catch (error) {
      return false;
    }
  }

  async getIndexedPagesCount(keyword) {
    try {
      const normalizedKeyword = keyword.toLowerCase().trim();
      const params = new URLSearchParams();
      params.append('q', '*:*');
      params.append('fq', `searchKeyword:"${normalizedKeyword}"`);
      params.append('rows', 0);
      params.append('wt', 'json');
      params.append('facet', 'true');
      params.append('facet.field', 'pageNumber');

      const response = await fetch(`${this.proxyUrl}/search?${params.toString()}`);
      const result = await response.json();

      const facetCounts = result.facet_counts?.facet_fields?.pageNumber || [];
      const pages = [];
      for (let i = 0; i < facetCounts.length; i += 2) {
        if (facetCounts[i + 1] > 0) {
          pages.push(Number(facetCounts[i]));
        }
      }

      return {
        pagesIndexed: pages.length,
        totalHits: result.response?.numFound || 0,
        pages: pages.sort((a, b) => a - b),
      };
    } catch (error) {
      return { pagesIndexed: 0, totalHits: 0, pages: [] };
    }
  }

  async storePaginationData(keyword, paginationData) {
    try {
      const normalizedKeyword = keyword.toLowerCase().trim();
      
      const doc = {
        id: `pagination_${normalizedKeyword.replace(/\s+/g, '_')}`,
        type: 'pagination',
        keyword: normalizedKeyword,
        totalPages: paginationData.totalPages || 1,
        totalItems: paginationData.totalItems || 0,
        pageSize: paginationData.pageSize || 50,
        pagesIndexed: paginationData.pagesIndexed || 1,
        indexedAt: Date.now(),
      };

      const response = await fetch(`${this.proxyUrl}/index`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([doc]),
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }

  async getPaginationData(keyword) {
    try {
      const normalizedKeyword = keyword.toLowerCase().trim();
      const objectID = `pagination_${normalizedKeyword.replace(/\s+/g, '_')}`;

      const params = new URLSearchParams();
      params.append('q', `id:"${objectID}"`);
      params.append('wt', 'json');

      const response = await fetch(`${this.proxyUrl}/search?${params.toString()}`);
      const result = await response.json();

      if (result.response?.docs?.length > 0) {
        const doc = result.response.docs[0];
        return {
          totalPages: doc.totalPages || 1,
          totalItems: doc.totalItems || 0,
          pageSize: doc.pageSize || 50,
          pagesIndexed: doc.pagesIndexed || 1,
          indexedAt: doc.indexedAt,
        };
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  async clearIndex() {
    try {
      const response = await fetch(`${this.proxyUrl}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: '*:*' }),
      });

      if (!response.ok) {
        throw new Error(`Failed to clear index: ${response.status}`);
      }
    } catch (error) {
      throw error;
    }
  }

  async clearKeywordCache(keyword) {
    try {
      const normalizedKeyword = keyword.toLowerCase().trim();
      
      const response = await fetch(`${this.proxyUrl}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: `searchKeyword:"${normalizedKeyword}"` }),
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }

  async storeProductDetails(offerId, productData) {
    try {
      const { mapped, variantsDetails, markupPercentage, priceRanges } = productData;

      if (!mapped) {
        return false;
      }

      const offerIdStr = String(offerId);
      const indexedAt = Date.now();
      const expiresAt = Date.now() + (24 * 60 * 60 * 1000);

      const doc = {
        id: `product_detail_${offerIdStr}`,
        type: 'product_detail',
        offerId: offerIdStr,
        title: mapped.title || '',
        sku: mapped.sku,
        sp: mapped.sp,
        consign_price: mapped.consign_price,
        image: mapped.image,
        imgUrl: mapped.imgUrl || [],
        description: mapped.description || '',
        location: mapped.location,
        handling_days: mapped.handling_days,
        month_sold: mapped.month_sold,
        moq: mapped.moq,
        categoryId: mapped.categoryId,
        companyName: mapped.companyName || '',
        supplierLogo: mapped.supplierLogo,
        soldOut: mapped.soldOut,
        markupPercentage: markupPercentage || 0,
        productAttribute: safeStringify(mapped.productAttribute || []),
        saleInfo: safeStringify(mapped.saleInfo || {}),
        sellerDataInfo: safeStringify(mapped.sellerDataInfo || {}),
        priceRanges: safeStringify(priceRanges || []),
        variantsDetails: safeStringify(variantsDetails || {}),
        indexedAt,
        expiresAt,
      };

      const response = await fetch(`${this.proxyUrl}/index`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([doc]),
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }

  async getProductDetails(offerId) {
    try {
      const offerIdStr = String(offerId);
      const objectID = `product_detail_${offerIdStr}`;

      const params = new URLSearchParams();
      params.append('q', `id:"${objectID}"`);
      params.append('wt', 'json');

      const response = await fetch(`${this.proxyUrl}/search?${params.toString()}`);
      const result = await response.json();

      if (!result.response?.docs?.length) {
        return null;
      }

      const doc = result.response.docs[0];

      if (doc.expiresAt && doc.expiresAt < Date.now()) {
        return null;
      }

      return {
        mapped: {
          sku: doc.sku,
          offerId: doc.offerId,
          title: doc.title,
          sp: doc.sp,
          consign_price: doc.consign_price,
          image: doc.image,
          imgUrl: doc.imgUrl || [],
          description: doc.description,
          location: doc.location,
          handling_days: doc.handling_days,
          month_sold: doc.month_sold,
          moq: doc.moq,
          categoryId: doc.categoryId,
          companyName: doc.companyName,
          supplierLogo: doc.supplierLogo,
          soldOut: doc.soldOut,
          productAttribute: safeParse(doc.productAttribute, []),
          saleInfo: safeParse(doc.saleInfo, {}),
          sellerDataInfo: safeParse(doc.sellerDataInfo, {}),
        },
        variantsDetails: safeParse(doc.variantsDetails, {}),
        markupPercentage: doc.markupPercentage,
        priceRanges: safeParse(doc.priceRanges, []),
      };
    } catch (error) {
      return null;
    }
  }
}

const solrServiceInstance = new SolrService();
export default solrServiceInstance;
export { mapProductToSolrDocument, safeStringify, safeParse };
